import React from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { useUser } from 'context/user/User';
import { useOnSelectMenu } from 'context/general/OnSelectMenu';
import DashHeader from './DashHeader';
import DashHeaderActions from './DashHeaderActions';
import paths from 'routes/paths';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';
import {
    UtensilsCrossed, Package, Calendar, ChefHat,
    ClipboardList, Wallet, Settings, BarChart2,
    Users, Tag, ArrowLeft, User, Gift, ChevronLeft, ChevronRight
} from 'lucide-react';

const navIcons = {
    profile:    User,
    menu:       UtensilsCrossed,
    inventory:  Package,
    posEvents:  Calendar,
    kitchen:    ChefHat,
    waiter:     ClipboardList,
    cashier:    Wallet,
    admin:      Settings,
    statistics: BarChart2,
    customers:  Users,
    promotions: Tag,
    affiliate:  Gift,
};

const sections = [
    {
        label: 'Operação',
        items: [
            { name: 'Perfil',    icon: 'profile',   route: paths.dashboard.establishment.profile },
            { name: 'Cardápio',  icon: 'menu',      route: paths.dashboard.establishment.menu },
            { name: 'Pedidos',   icon: 'kitchen',   route: paths.dashboard.establishment.kitchen },
            { name: 'Comandas',  icon: 'waiter',    route: paths.dashboard.establishment.waiter },
            { name: 'Caixa',     icon: 'cashier',   route: paths.dashboard.establishment.cashier },
            { name: 'Eventos',   icon: 'posEvents', route: paths.dashboard.establishment.posEvents },
        ],
    },
    {
        label: 'Gestão',
        items: [
            { name: 'Estoque',        icon: 'inventory',  route: paths.dashboard.establishment.inventory },
            { name: 'Promoções',      icon: 'promotions', route: paths.dashboard.establishment.promotions },
            { name: 'Administração',  icon: 'admin',      route: paths.dashboard.establishment.admin },
            { name: 'Indicações',     icon: 'affiliate',  route: paths.dashboard.establishment.affiliate },
        ],
    },
    {
        label: 'Análise',
        items: [
            { name: 'Estatísticas', icon: 'statistics', route: paths.dashboard.establishment.statistics },
            { name: 'Clientes',     icon: 'customers',  route: paths.dashboard.establishment.customers },
        ],
    },
];

export default function DashEstablishmentSlot({ children }) {
    const { isLateralMenuActive, setIsLateralMenuActive, isSidebarCollapsed, toggleSidebarCollapsed } = useOnSelectMenu();
    const location = useLocation();
    const currentPath = location.pathname;

    function closeMobileMenu() {
        setIsLateralMenuActive(false);
    }

    return (
        <Container>
            <DashHeader visible />
            <DashHeaderActions />

            {isLateralMenuActive && <MobileOverlay onClick={closeMobileMenu} />}

            <Sidebar active={isLateralMenuActive} $collapsed={isSidebarCollapsed}>
                <SidebarInner>
                    <BackLink to={paths.dashboard.home} $collapsed={isSidebarCollapsed} title={isSidebarCollapsed ? 'Voltar ao painel' : undefined}>
                        <ArrowLeft size={14} />
                        {!isSidebarCollapsed && 'Voltar ao painel'}
                    </BackLink>

                    {!isSidebarCollapsed && <SidebarTitle>Estabelecimento</SidebarTitle>}

                    {sections.map((section, si) => (
                        <div key={si}>
                            <SectionDivider />
                            {!isSidebarCollapsed && <SectionLabel>{section.label}</SectionLabel>}
                            <NavSection>
                                {section.items.map(item => {
                                    const Icon = navIcons[item.icon];
                                    const isSelected = currentPath === item.route;
                                    return (
                                        <NavItem
                                            key={item.route}
                                            to={item.route}
                                            selected={isSelected}
                                            $collapsed={isSidebarCollapsed}
                                            title={isSidebarCollapsed ? item.name : undefined}
                                        >
                                            <NavIconWrapper selected={isSelected}>
                                                {Icon && <Icon size={16} />}
                                            </NavIconWrapper>
                                            {!isSidebarCollapsed && <NavLabel>{item.name}</NavLabel>}
                                        </NavItem>
                                    );
                                })}
                            </NavSection>
                        </div>
                    ))}
                </SidebarInner>
            </Sidebar>

            <SidebarToggle
                type='button'
                onClick={toggleSidebarCollapsed}
                $collapsed={isSidebarCollapsed}
                title={isSidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
            >
                {isSidebarCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
            </SidebarToggle>

            <MainContent>
                <Content>
                    {children}
                </Content>
            </MainContent>
        </Container>
    );
}

const Container = styled.div`
    height: auto;
    width: 100%;
    flex-direction: column;

    @media screen and (min-width: ${breakpoints.sm}) {
        display: flex;
        width: 100%;
        flex-direction: row;
    }
`;

const MobileOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.3);
    z-index: 30;
    backdrop-filter: blur(2px);

    @media screen and (min-width: ${breakpoints.lg}) {
        display: none;
    }
`;

const Sidebar = styled.nav`
    background: #fff;
    width: 0;
    opacity: 0;
    height: 100vh;
    display: flex;
    flex-direction: column;
    padding-top: 100px;
    overflow: hidden;
    position: fixed;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    left: 0;
    top: 0;
    z-index: 31;
    border-right: 1.5px solid ${colors.night}15;

    @media screen and (min-width: ${breakpoints.lg}) {
        opacity: 1 !important;
        width: ${({ $collapsed }) => $collapsed ? '76px' : '240px'};
        padding-top: 110px;
        box-shadow: 0 1px 8px ${colors.night}08;
    }

    ${({ active }) => active && `
        width: 280px;
        opacity: 1;
        box-shadow: 4px 0 24px ${colors.night}12;
    `}
`;

const SidebarToggle = styled.button`
    display: none;
    padding: 0;
    border: 1.5px solid ${colors.night}15;
    background: #fff;
    color: ${colors.night}88;
    cursor: pointer;

    @media screen and (min-width: ${breakpoints.lg}) {
        display: flex;
        align-items: center;
        justify-content: center;
        position: fixed;
        top: 128px;
        left: ${({ $collapsed }) => $collapsed ? '64px' : '228px'};
        width: 24px;
        height: 24px;
        border-radius: 50%;
        box-shadow: 0 1px 4px ${colors.night}15;
        z-index: 32;
        transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);

        &:hover {
            background: ${colors.night}08;
            color: ${colors.nightDark};
        }
    }
`;

const SidebarInner = styled.div`
    display: flex;
    flex-direction: column;
    padding: 16px 14px 20px;
    overflow-y: auto;
    flex: 1;

    &::-webkit-scrollbar { width: 3px; }
    &::-webkit-scrollbar-thumb {
        background: ${colors.night}22;
        border-radius: 10px;
    }
`;

const BackLink = styled(Link)`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: ${colors.night}88;
    text-decoration: none;
    padding: 6px 10px;
    border-radius: 8px;
    margin-bottom: 10px;
    transition: background 0.15s, color 0.15s;

    ${({ $collapsed }) => $collapsed && `
        justify-content: center;
        padding: 6px 0;
    `}

    &:hover {
        background: ${colors.night}08;
        color: ${colors.nightDark};
    }
`;

const SidebarTitle = styled.div`
    font-size: 15px;
    font-weight: 600;
    color: ${colors.nightDark};
    padding: 0 10px;
    margin-bottom: 4px;
`;

const SectionDivider = styled.div`
    height: 1px;
    background: ${colors.night}11;
    margin: 10px 10px 8px;
`;

const SectionLabel = styled.div`
    font-size: 10px;
    font-weight: 600;
    color: ${colors.night}66;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    padding: 0 14px;
    margin-bottom: 4px;
`;

const NavSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1px;
`;

const NavItem = styled(Link).withConfig({
    shouldForwardProp: (prop) => !['selected'].includes(prop),
})`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 5px 14px;
    border-radius: 10px;
    cursor: pointer;
    text-decoration: none;
    font-size: 14px;
    font-weight: ${({ selected }) => selected ? '600' : '500'};
    color: ${({ selected }) => selected ? panelAccent(colors.occasionalPurple) : colors.nightDark};
    background: ${({ selected }) => selected ? panelAccentAlpha(colors.occasionalPurple, '0A') : 'transparent'};
    transition: all 0.15s ease;

    ${({ $collapsed }) => $collapsed && `
        justify-content: center;
        padding: 5px 0;
    `}

    &:hover {
        background: ${({ selected }) => selected ? panelAccentAlpha(colors.occasionalPurple, '0F') : colors.night + '08'};
        color: ${({ selected }) => selected ? panelAccent(colors.occasionalPurple) : colors.nightDark};
    }
`;

const NavIconWrapper = styled.div.withConfig({
    shouldForwardProp: (prop) => !['selected'].includes(prop),
})`
    width: 30px;
    height: 30px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: ${({ selected }) => selected ? panelAccentAlpha(colors.occasionalPurple, '15') : colors.night + '08'};
    color: ${({ selected }) => selected ? panelAccent(colors.occasionalPurple) : colors.night};
    transition: all 0.15s ease;
`;

const NavLabel = styled.span`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const MainContent = styled.main`
    width: 100%;
    padding-top: 120px;
    min-height: 100vh;
    background: ${colors.neutralBackground};

    @media screen and (min-width: ${breakpoints.lg}) {
        min-height: 100vh;
    }
`;

const Content = styled.div`
    display: flex;
    border-radius: 8px;
    min-height: calc(100% - 115px);
    padding: 0 20px 20px 20px;
    background: ${colors.neutralBackground};
    justify-content: space-between;
`;
